import * as React from "react";
import { Formik, FormikErrors, FormikState, FormikTouched } from "formik";
import TextField, { TextFieldInput, TextFieldLabel } from "../core/TextField";
import TextArea from "../core/TextArea";
import Select from "react-select";
import { companyInfoGrabValidationSchema } from "../../libs/onboard";
import Component from "@reach/component-component";
import { isEqual } from "lodash";
import { caProvinces, usStates } from "../../libs/regions";
import NumberFormat from "react-number-format";

type FormValues = Partial<ICompanyInfoGrab>;

const defaultFormValues: FormValues = {
  country: "USA",
  stateOrProvince: "AL",
  lobTools: "",
  books: "",
  lengthOfBusiness: "1-2",
  marketPopulation: "",
  clientsOrEndpoints: "",
  avgClientSize: "",
  remoteLocations: "",
  pctMgdService: "",
  techCerts: "",
  growthGoalsAndEndYrStrat: "",
  currentBusinessIssues: "",
  keepDoing: "",
  salesStruggles: "",
  marketingStruggles: "",
  operationsStruggles: "",
  annualRevenue: "<1m",
  orgSize: "1-5",
  missionStatement: "",
};

type AnnualRevenueOpt = ISelectOption<CompanyAnnualRevenue>;

const annualRevenueOpts: AnnualRevenueOpt[] = [
  { label: "$20 million or more", value: ">=20m" },
  { label: "$15 million to $19.9 million", value: "15m-19.9m" },
  { label: "$10 million to $14.9 million", value: "10m-14.9m" },
  { label: "$5 million to $9.9 million", value: "5m-9.9m" },
  { label: "$1 million to $4.9 million", value: "1m-4.9m" },
  { label: "Under $1 million", value: "<1m" },
];

type OrgSizeOpt = ISelectOption<OrgSize>;

const orgSizeOpts: OrgSizeOpt[] = [
  { label: "26+ employees", value: "26+" },
  { label: "16-25 employees", value: "16-25" },
  { label: "11-15 employees", value: "11-15" },
  { label: "6-10 employees", value: "6-10" },
  { label: "1-5 employees", value: "1-5" },
];

type CountryOpt = ISelectOption<CompanyCountry>;

const countryOpts: CountryOpt[] = [
  { label: "United States of America", value: "USA" },
  { label: "Canada", value: "CANADA" },
  { label: "Other", value: "OTHER" },
];

const usStatesOpts = usStates.map(s => ({ label: s.name, value: s.iso }));

const caProvincesOpts = caProvinces.map(p => ({ label: p.name, value: p.iso }));

type BusinessLengthOpt = ISelectOption<CompanyBusinessLength>;

const businessLengthOpts: BusinessLengthOpt[] = [
  { label: "1-2 years", value: "1-2" },
  { label: "3-5 years", value: "3-5" },
  { label: "6-10 years", value: "6-10" },
  { label: "10+ years", value: "10+" },
];

interface FormState {
  values: FormValues;
  errors: FormikErrors<FormValues>;
  touched: FormikTouched<FormValues>;
}

interface IProps {
  infoGrabData?: ICompanyInfoGrab;
  readOnly?: boolean;
  onChange?: (prevState: FormState, currentState: FormState) => void;
}

interface IState {}

class CompanyInfoGrabForm extends React.Component<IProps, IState> {
  render() {
    const { infoGrabData, onChange, readOnly } = this.props;

    const initialValues = infoGrabData ? infoGrabData : defaultFormValues;

    return (
      <Formik
        initialValues={initialValues}
        onSubmit={() => {}}
        validationSchema={companyInfoGrabValidationSchema}
        render={({ values, handleChange, errors, setFieldValue, touched }) => (
          <form>
            {onChange && (
              <Component
                formik={{ values, errors, touched }}
                didUpdate={({
                  prevProps,
                  props,
                }: {
                  prevProps: { formik: FormState };
                  props: { formik: FormState };
                }) => {
                  if (!isEqual(prevProps.formik, props.formik)) {
                    onChange(prevProps.formik, props.formik);
                  }
                }}
              />
            )}
            <TextFieldLabel label={"Country"} />
            <Select
              value={countryOpts.find(o => values.country === o.value)}
              clearable={false}
              options={countryOpts}
              onChange={(opt: CountryOpt) => {
                setFieldValue("country", opt.value);
                setFieldValue("stateOrProvince", "");
              }}
              disabled={readOnly}
            />
            {!values.country && (
              <>
                <TextFieldLabel label={"State"} />
                <Select
                  clearable={false}
                  disabled={true}
                  placeholder={"Select a country first..."}
                />
              </>
            )}
            {values.country === "USA" && (
              <>
                <TextFieldLabel label={"State"} />
                <Select
                  value={usStatesOpts.find(
                    o => values.stateOrProvince === o.value
                  )}
                  clearable={false}
                  options={usStatesOpts}
                  onChange={(opt: typeof usStatesOpts[0]) =>
                    setFieldValue("stateOrProvince", opt.value)
                  }
                  disabled={readOnly}
                />
              </>
            )}
            {values.country === "CANADA" && (
              <>
                <TextFieldLabel label={"Province"} />
                <Select
                  value={caProvincesOpts.find(
                    o => values.stateOrProvince === o.value
                  )}
                  clearable={false}
                  options={caProvincesOpts}
                  onChange={(opt: typeof caProvincesOpts[0]) =>
                    setFieldValue("stateOrProvince", opt.value)
                  }
                  disabled={readOnly}
                />
              </>
            )}
            <TextField
              label={"L.O.B. Apps/Tools In Use"}
              value={values.lobTools}
              type={"text"}
              name={"lobTools"}
              onChange={handleChange}
              readOnly={readOnly}
            />
            <TextArea
              label={`List Three (3) Books Your Company Turns to for "Adopted Methodologies" or "Beliefs"`}
              value={values.books}
              name={"books"}
              onChange={handleChange}
              readOnly={readOnly}
            />
            <TextFieldLabel label={"How long have you been in business?"} />
            <Select
              value={businessLengthOpts.find(
                o => values.lengthOfBusiness === o.value
              )}
              clearable={false}
              options={businessLengthOpts}
              onChange={(opt: typeof businessLengthOpts[0]) =>
                setFieldValue("lengthOfBusiness", opt.value)
              }
              disabled={readOnly}
            />
            <TextFieldLabel
              label={"What is the population in your marketplace?"}
            />
            <NumberFormat
              value={values.marketPopulation}
              onValueChange={values =>
                setFieldValue("marketPopulation", values.value)
              }
              thousandSeparator={true}
              decimalScale={0}
              allowEmptyFormatting={true}
              isNumericString={true}
              customInput={TextFieldInput}
            />
            <TextFieldLabel
              label={"How many clients/end points do you manage?"}
            />
            <NumberFormat
              value={values.clientsOrEndpoints}
              onValueChange={values =>
                setFieldValue("clientsOrEndpoints", values.value)
              }
              thousandSeparator={true}
              decimalScale={0}
              allowEmptyFormatting={true}
              isNumericString={true}
              customInput={TextFieldInput}
            />
            <TextFieldLabel label={"What is your average client size?"} />
            <NumberFormat
              value={values.avgClientSize}
              onValueChange={values =>
                setFieldValue("avgClientSize", values.value)
              }
              thousandSeparator={true}
              decimalScale={0}
              allowEmptyFormatting={true}
              isNumericString={true}
              customInput={TextFieldInput}
            />
            <TextField
              label={"Do you have any remote locations, and if yes, where?"}
              value={values.remoteLocations}
              type={"text"}
              name={"remoteLocations"}
              onChange={handleChange}
              readOnly={readOnly}
            />
            <TextFieldLabel
              label={"What percentage of your business is Managed Service?"}
            />
            <NumberFormat
              value={values.pctMgdService}
              onValueChange={values =>
                setFieldValue("pctMgdService", values.value)
              }
              decimalScale={0}
              allowEmptyFormatting={true}
              suffix={"%"}
              isNumericString={true}
              customInput={TextFieldInput}
            />

            <TextField
              label={"What technical certifications does your team carry?"}
              value={values.techCerts}
              type={"text"}
              name={"techCerts"}
              onChange={handleChange}
              readOnly={readOnly}
            />
            <TextField
              label={
                "What are your company's goals for growth and your year-end strategy?"
              }
              value={values.growthGoalsAndEndYrStrat}
              type={"text"}
              name={"growthGoalsAndEndYrStrat"}
              onChange={handleChange}
              readOnly={readOnly}
            />
            <TextField
              label={"Current business issues:"}
              value={values.currentBusinessIssues}
              type={"text"}
              name={"currentBusinessIssues"}
              onChange={handleChange}
              readOnly={readOnly}
            />
            <TextField
              label={"What should your business keep doing?"}
              value={values.keepDoing}
              type={"text"}
              name={"keepDoing"}
              onChange={handleChange}
              readOnly={readOnly}
            />
            <TextField
              label={"Current sales struggles:"}
              value={values.salesStruggles}
              type={"text"}
              name={"salesStruggles"}
              onChange={handleChange}
              readOnly={readOnly}
            />
            <TextField
              label={"Current marketing struggles:"}
              value={values.marketingStruggles}
              type={"text"}
              name={"marketingStruggles"}
              onChange={handleChange}
              readOnly={readOnly}
            />
            <TextField
              label={"Current operations struggles:"}
              value={values.operationsStruggles}
              type={"text"}
              name={"operationsStruggles"}
              onChange={handleChange}
              readOnly={readOnly}
            />
            <TextFieldLabel label={"Annual Revenue in Dollars"} />
            <Select
              value={annualRevenueOpts.find(
                o => values.annualRevenue === o.value
              )}
              clearable={false}
              options={annualRevenueOpts}
              onChange={(opt: AnnualRevenueOpt) =>
                setFieldValue("annualRevenue", opt.value)
              }
              disabled={readOnly}
            />
            <TextFieldLabel label={"Size of your Organization"} />
            <Select
              value={orgSizeOpts.find(o => values.orgSize === o.value)}
              clearable={false}
              onChange={(opt: OrgSizeOpt) =>
                setFieldValue("orgSize", opt.value)
              }
              options={orgSizeOpts}
              disabled={readOnly}
            />
            <TextArea
              label={"Your Mission Statement"}
              value={values.missionStatement}
              name={"missionStatement"}
              onChange={handleChange}
              readOnly={readOnly}
            />
          </form>
        )}
      />
    );
  }
}

export default CompanyInfoGrabForm;



// WEBPACK FOOTER //
// ./app/javascript/components/onboarding/CompanyInfoGrabForm.tsx